/**
 * Screen 9 — Wallet.
 *
 * Violet hero with the available balance + Deposit/Withdraw CTAs side by
 * side, then a card listing recent transactions with up/down direction
 * icons. Money formatting flows through `wallet.js` so the integer-kobo
 * invariant never leaks to the UI.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { icon, I } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { walletShopBannerHtml } from '../components/wallet_shop_banner.js';
import { navigate } from '../router.js';
import { formatDate, formatKobo, txnKindLabel, txnStatusClass, txnStatusLabel } from '../wallet.js';

const INCOMING_KINDS = new Set([
  'deposit',
  'withdrawal_reversal',
  'contest_refund',
  'contest_settle',
]);

const PAGE_SIZE = 20;

export async function renderWallet(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Wallet</div>
    </div>
    <div class="pad" id="wallet-body">
      <div class="hero violet center">
        <div style="font-size:12px;font-weight:500;opacity:0.85;">Available balance</div>
        <div class="big" style="font-size:34px;margin:4px 0;font-weight:700;color:#fff;" id="wallet-balance">—</div>
        <div class="row" style="gap:9px;margin-top:6px;">
          <a href="/wallet/deposit" data-link class="btn gold" style="flex:1;margin:0;">Deposit</a>
          <a href="/wallet/withdraw" data-link class="btn" style="flex:1;margin:0;background:rgba(255,255,255,0.16);border-bottom-color:rgba(0,0,0,0.2);">Withdraw</a>
        </div>
      </div>
      <div id="wallet-bonus-card" class="card" style="margin-top:10px;padding:12px 14px;background:rgba(233,75,162,0.08);border:1px solid rgba(233,75,162,0.45);">
        <div class="row" style="align-items:center;gap:10px;">
          <div style="font-size:24px;line-height:1;">🎁</div>
          <div class="grow" style="min-width:0;">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#E94BA2;font-weight:700;">
              Bonus credit
            </div>
            <div style="font-family:var(--font-display);font-size:22px;color:#fff;font-weight:700;line-height:1.1;margin-top:2px;" id="wallet-bonus-balance">—</div>
            <div class="muted" style="font-size:11px;margin-top:3px;line-height:1.35;">
              Spends on duels &amp; power-ups. <b style="color:#E94BA2;">Win a duel to convert to cash.</b>
            </div>
          </div>
        </div>
      </div>
      ${walletShopBannerHtml()}
      <div class="center" style="margin-top:6px;font-size:12px;">
        <a href="/wallet/banks" data-link>Manage bank accounts →</a>
      </div>
      <h3 class="sec">Recent activity</h3>
      <div id="wallet-history-summary" class="muted" style="font-size:11px;margin-bottom:6px;"></div>
      <div id="wallet-history">
        <div class="skel"></div>
        <div class="skel"></div>
        <div class="skel"></div>
      </div>
      <div id="wallet-history-more" style="margin-top:10px;"></div>
    </div>
  `;

  try {
    const [wallet, list] = await Promise.all([
      api('/wallet'),
      api(`/wallet/transactions?limit=${PAGE_SIZE}&offset=0`),
    ]);
    document.getElementById('wallet-balance').textContent = formatKobo(wallet.balance_kobo);
    // Bonus card always visible so users discover the feature even before
    // their first live-trivia win lands credit there. Shows ₦0.00 in the
    // empty state — the explanatory copy underneath ("spends first on duels
    // & power-ups, not withdrawable") doubles as a teaser for what fills
    // it up.
    document.getElementById('wallet-bonus-balance').textContent =
      formatKobo(Number(wallet.bonus_balance_kobo || 0));
    // State held in closure — kept here (not module-level) so navigating
    // away and back resets cleanly without a stale offset bleeding in.
    const state = { items: list.items, total: list.total, hasMore: list.has_more };
    renderHistory(state);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    if (err instanceof ApiError && err.code === 'email_not_verified') {
      document.getElementById('wallet-body').innerHTML = `
        <div class="note gold" style="margin-top:18px;">
          Verify your email to unlock the wallet.
          <a href="/verify-email" data-link style="color:inherit;text-decoration:underline;">Verify now</a>.
        </div>
      `;
      return;
    }
    toast.fromError(err, 'Could not load wallet.');
    document.getElementById('wallet-history').innerHTML =
      `<div class="card muted">Could not load activity.</div>`;
  }
}

function renderHistory(state) {
  const host = document.getElementById('wallet-history');
  const more = document.getElementById('wallet-history-more');
  const summary = document.getElementById('wallet-history-summary');
  if (!state.items.length) {
    summary.textContent = '';
    host.innerHTML = `
      <div class="card center muted">
        No transactions yet. Tap <strong>Deposit</strong> to top up and start playing.
      </div>
    `;
    more.innerHTML = '';
    return;
  }
  summary.textContent =
    `Showing ${state.items.length} of ${state.total.toLocaleString('en-NG')}`;
  host.innerHTML = state.items.map(renderRow).join('');
  more.innerHTML = state.hasMore
    ? `<button type="button" class="button-secondary button-inline" id="load-more-btn" style="width:100%;">Load more</button>`
    : '';
  const btn = document.getElementById('load-more-btn');
  if (btn) btn.addEventListener('click', () => loadMore(state));
}

async function loadMore(state) {
  const btn = document.getElementById('load-more-btn');
  btn.disabled = true;
  btn.textContent = 'Loading…';
  try {
    const page = await api(`/wallet/transactions?limit=${PAGE_SIZE}&offset=${state.items.length}`);
    state.items = state.items.concat(page.items);
    state.total = page.total;
    state.hasMore = page.has_more;
    renderHistory(state);
  } catch (err) {
    toast.fromError(err, 'Could not load more.');
    btn.disabled = false;
    btn.textContent = 'Load more';
  }
}

function renderRow(t) {
  const incoming = INCOMING_KINDS.has(t.kind);
  const sign = incoming ? '+' : '−';
  const isMoved = t.status === 'succeeded' || (t.kind === 'withdrawal' && t.status === 'pending');
  // Direction icon stays meaningful for pending: pending deposit is still
  // "money coming in" (just not credited yet), pending withdrawal is "money
  // going out" (and it IS held — the wallet is debited at request time).
  const dirIcon = incoming
    ? `<div class="ic teal">${icon(I.arrowDown, { size: 16 })}</div>`
    : `<div class="ic" style="background:#2E1622;color:var(--pink-t);">${icon(I.arrowUp, { size: 16 })}</div>`;
  const statusLabel = txnStatusLabel(t.kind, t.status);
  const statusBadge = statusLabel
    ? `<span class="status-pill ${txnStatusClass(t.status)}" style="margin-left:6px;font-size:10px;">${escape(statusLabel)}</span>`
    : '';
  const sub = t.provider
    ? `${escape(t.provider)} · ${escape(formatDate(t.created_at))}`
    : escape(formatDate(t.created_at));
  // Mute the amount when the money hasn't actually moved (pending deposits,
  // failed/cancelled rows). Withdrawal pending IS a real debit, so we keep
  // the full-colour amount.
  const amountClass = incoming ? 'txn-row-amount up' : 'txn-row-amount dn';
  const amountStyle = isMoved ? '' : 'opacity:0.55;';
  return `
    <div class="card" style="padding:12px;">
      <div class="row">
        ${dirIcon}
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">
            ${escape(txnKindLabel(t.kind))}${statusBadge}
          </b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">${sub}</div>
        </div>
        <b class="${amountClass}" style="${amountStyle}">${sign}${escape(formatKobo(t.amount_kobo))}</b>
      </div>
    </div>
  `;
}
